import React, { useEffect } from "react";
import { BiTargetLock } from "react-icons/bi";
import { CnLinkedLhcsResponse, getVehicleDetails } from "../api/shipments";
import { formatDate, titleCase } from "../../../lib/utils";
import { HiArrowTopRightOnSquare } from "react-icons/hi2";

interface TripDetailsProps {
  data: CnLinkedLhcsResponse | null;
  loading: boolean;
  error: string | null;
}

const TripDetails: React.FC<TripDetailsProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mb-2"></div>
        <p className="text-sm">Loading trip details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-red-600">
        <p className="text-sm font-medium">Failed to load trip details.</p>
        <p className="text-xs text-gray-500 mt-1">{error}</p>
      </div>
    );
  }

  if (!data || !data.lhcs || data.lhcs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-600">
        <p className="text-sm">No trip details available.</p>
      </div>
    );
  }

  const sortedTrips = [...data.lhcs].sort((a, b) => {
    const dateA = new Date(a.lhc_date).getTime();
    const dateB = new Date(b.lhc_date).getTime();
    return dateB - dateA; 
  });

  const handleVehicleClick = (vehicleNo: string) => {
    window.open(`/reports/vehicle-details?vehicleNumber=${encodeURIComponent(vehicleNo)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="flex flex-col space-y-4">
      {sortedTrips.map((trip, index) => (
        <div
          key={index}
          className="bg-gray-50 p-5 rounded-lg shadow-sm"
        >
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              {trip.lhc_number || "—"},
              <span className="text-gray-900 font-medium ml-2">
                {formatDate(trip.lhc_date) || "-"}
              </span>
            </h4>
            <h6 className="text-sm mt-1 text-gray-900">{trip.branch_name}</h6>
          </div>

          <div className="flex items-stretch gap-4">
            {/* Left column: icons + line */}
            <div className="flex flex-col items-center justify-between flex-shrink-0">
              <BiTargetLock className="text-green-500 text-2xl" />
              <div className="w-[2px] h-24 bg-gray-200" />
              <BiTargetLock className="text-red-500 text-2xl" />
            </div>

            {/* Middle group: origin/destination + driver/vehicle */}
            <div className="flex flex-col justify-between flex-1">
              <div className="text-sm text-gray-700 font-medium">
                {trip.origin || "—"}
              </div>

              {/* Middle: driver/vehicle (centered between origin and destination) */}
              <div className="flex justify-start text-sm text-gray-700 pl-3">
                <div className="flex flex-col items-start justify-center">
                  <div>
                    <span className="font-medium text-gray-900">
                        Distance:{" "} 
                        <span className="text-gray-700">{trip.lhc_distance_km} km</span>
                    </span>
                  </div>
                  <div className="mt-1">
                    <span onClick={() => trip.vehicle_no && handleVehicleClick(trip.vehicle_no)} className="inline-flex items-center gap-1 font-semibold text-gray-900 underline hover:font-bold cursor-pointer">
                      {trip.vehicle_no || "N/A"}
                      {trip.vehicle_no && (<HiArrowTopRightOnSquare className="h-4 w-4 font-bold" />)}
                    </span>{" "}
                    – {trip.vehicle_type || "N/A"}
                  </div>
                  <div className="mt-1">
                    <span className="font-medium text-gray-900">
                      {trip.driver_name ? titleCase(trip.driver_name) : "N/A"}
                    </span>{" "}
                    – {trip.driver_mobile || "N/A"}
                  </div>
                </div>
              </div>

              {/* Bottom: destination */}
              <div className="text-sm text-gray-700 font-medium">
                {trip.destination || "—"}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TripDetails;
